import { FilterMethod, parseFilter } from '../data-table';

const SEARCH_ID_CAP = 100;
const COMMENT_PAGE_SIZE = 100;

/** Where the task-list search box looks. Exactly one at a time — see the search popover. */
export type TaskSearchIn = 'subject' | 'body' | 'coding';

/**
 * Build the FTS:… value for a full-text filter (same path as mail OCR FullText).
 * Uses CT (contains all words) so multi-word queries require each token.
 */
function buildFtsValue(filterId: string, query: string): string {
    const trimmed = query.trim();
    if (!trimmed) {
        return '';
    }
    return parseFilter({
        filterId,
        filterType: 'FullText',
        filterMethod: FilterMethod.CT,
        value: trimmed,
    });
}

/** FTS value for `filterBody` on GET /comment. */
export function buildCommentBodyFtsValue(query: string): string {
    return buildFtsValue('filterBody', query);
}

/** FTS value for `filterResult` on GET /coding. */
export function buildCodingResultFtsValue(query: string): string {
    return buildFtsValue('filterResult', query);
}

export interface ICommentSearchHit {
    messageId?: string;
}

export interface ICodingSearchHit {
    id?: string;
    claimId?: string;
}

/**
 * Page through an FTS endpoint and collect unique IDs (capped).
 *
 * The cap is the ceiling of the whole "search a child resource, then filter tasks by the IDs it
 * returns" approach: those IDs travel in the query string, so it cannot be raised far. It is also
 * applied BEFORE the open/closed tab filter, so a term matching mostly closed tasks spends the cap
 * on them. Both are known limits, not accidents — see the search notes in task-list-fetch.
 */
async function collectIdsFromFtsSearch<THit>(options: {
    ftsValue: string;
    fetchPage: (args: { fts: string; limit: number; offset: number }) => Promise<THit[]>;
    readId: (hit: THit) => string | undefined;
    cap?: number;
}): Promise<string[]> {
    if (!options.ftsValue) {
        return [];
    }
    const cap = options.cap ?? SEARCH_ID_CAP;
    const ids = new Set<string>();
    let offset = 0;

    while (ids.size < cap) {
        const page = await options.fetchPage({
            fts: options.ftsValue,
            limit: COMMENT_PAGE_SIZE,
            offset,
        });
        if (!page.length) {
            break;
        }
        for (const hit of page) {
            const id = options.readId(hit);
            if (id) {
                ids.add(id);
                if (ids.size >= cap) {
                    break;
                }
            }
        }
        if (page.length < COMMENT_PAGE_SIZE) {
            break;
        }
        offset += COMMENT_PAGE_SIZE;
    }

    return [...ids];
}

/**
 * Page through comment FTS results and collect unique message IDs (capped).
 */
export async function collectMessageIdsFromCommentSearch(options: {
    query: string;
    searchComments: (args: {
        filterBody: string[];
        limit: number;
        offset: number;
    }) => Promise<ICommentSearchHit[]>;
    cap?: number;
}): Promise<string[]> {
    return collectIdsFromFtsSearch({
        ftsValue: buildCommentBodyFtsValue(options.query),
        fetchPage: ({ fts, limit, offset }) =>
            options.searchComments({ filterBody: [fts], limit, offset }),
        readId: hit => hit.messageId,
        cap: options.cap,
    });
}

/**
 * Page through coding FTS results and collect the unique CLAIM IDs they belong to (capped).
 *
 * Claim, not coding: measured 2026-09-15 on a live account, coding-problem tasks carry a `claims`
 * link (50/50 sampled) and never a `codings` link (0/50), so `filterCodingId` on GET /message has
 * nothing to match. Every coding carries `claimId`, and filtering messages by claim is the same
 * path the Patient search already uses.
 */
export async function collectClaimIdsFromCodingSearch(options: {
    query: string;
    searchCodings: (args: {
        filterResult: string[];
        limit: number;
        offset: number;
    }) => Promise<ICodingSearchHit[]>;
    cap?: number;
}): Promise<string[]> {
    return collectIdsFromFtsSearch({
        ftsValue: buildCodingResultFtsValue(options.query),
        fetchPage: ({ fts, limit, offset }) =>
            options.searchCodings({ filterResult: [fts], limit, offset }),
        readId: hit => hit.claimId,
        cap: options.cap,
    });
}

/**
 * One query-keyed, single-flight cache of FTS → IDs.
 *
 * Shared by the comment and coding searches on purpose: the supersede logic below (ignore a
 * resolution once a newer query has replaced it) is the part that goes subtly wrong when copied.
 */
function createFtsIdCache(): {
    get: (term: string, run: () => Promise<string[]>) => Promise<string[]>;
    clear: () => void;
} {
    let cacheQuery: string | null = null;
    let cacheIds: string[] | null = null;
    let inflight: Promise<string[]> | null = null;

    const clear = (): void => {
        cacheQuery = null;
        cacheIds = null;
        inflight = null;
    };

    return {
        clear,
        get: (term, run) => {
            if (cacheQuery === term && cacheIds) {
                return Promise.resolve(cacheIds);
            }
            if (cacheQuery === term && inflight) {
                return inflight;
            }

            cacheQuery = term;
            cacheIds = null;
            let pending: Promise<string[]>;
            pending = run().then(
                ids => {
                    // Ignore writes after clear or a newer query superseded this request.
                    if (cacheQuery === term && inflight === pending) {
                        cacheIds = ids;
                        inflight = null;
                    }
                    return ids;
                },
                error => {
                    if (cacheQuery === term && inflight === pending) {
                        inflight = null;
                    }
                    throw error;
                },
            );
            inflight = pending;

            return pending;
        },
    };
}

const commentFtsCache = createFtsIdCache();
const codingFtsCache = createFtsIdCache();

/** Drop every cached search → ID mapping. Call when the search input text changes. */
export function clearTaskSearchIdCaches(): void {
    commentFtsCache.clear();
    codingFtsCache.clear();
}

/**
 * Return cached comment FTS message IDs for `query`, or run FTS once and cache.
 * Call {@link clearTaskSearchIdCaches} when the search input text changes.
 */
export async function getCachedCommentFtsMessageIds(options: {
    query: string;
    searchComments: (args: {
        filterBody: string[];
        limit: number;
        offset: number;
    }) => Promise<ICommentSearchHit[]>;
    cap?: number;
}): Promise<string[]> {
    const term = options.query.trim();
    if (!term) {
        clearTaskSearchIdCaches();
        return [];
    }

    return commentFtsCache.get(term, () =>
        collectMessageIdsFromCommentSearch({
            query: term,
            searchComments: options.searchComments,
            cap: options.cap,
        }),
    );
}

/**
 * Return cached claim IDs for a coding-result `query`, or run FTS once and cache.
 * Call {@link clearTaskSearchIdCaches} when the search input text changes.
 */
export async function getCachedCodingFtsClaimIds(options: {
    query: string;
    searchCodings: (args: {
        filterResult: string[];
        limit: number;
        offset: number;
    }) => Promise<ICodingSearchHit[]>;
    cap?: number;
}): Promise<string[]> {
    const term = options.query.trim();
    if (!term) {
        clearTaskSearchIdCaches();
        return [];
    }

    return codingFtsCache.get(term, () =>
        collectClaimIdsFromCodingSearch({
            query: term,
            searchCodings: options.searchCodings,
            cap: options.cap,
        }),
    );
}
